'use client';
import { useState } from "react";
import { Check, Zap, Coins, Info, AlertCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useTranslations } from "next-intl";

import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useParams } from "next/navigation";
import { type Locale } from "@/lib/i18n/config";
import { useLoading } from "@/lib/contexts/LoadingContext";

export default function PricingPage() {
    const t = useTranslations("pricingPage");
    const router = useRouter();
    const params = useParams();
    const locale = params.locale as Locale;
    const [loading, setLoading] = useState<string | null>(null);
    const { withLoading } = useLoading();

    const creditPacks = [
        {
            id: 'starter',
            name: t('packs.starter.name'),
            price: "$1.00",
            credits: 7000,
            tasks: 70,
            description: t('packs.starter.description'),
            variantIdKey: "LEMONSQUEEZY_VARIANT_ID_CREDITS_7000",
            features: [
                t('packs.starter.features.f1'),
                t('packs.starter.features.f2'),
                t('packs.starter.features.f3'),
                t('packs.starter.features.f4')
            ],
            color: "slate",
            highlight: false
        },
        {
            id: 'standard',
            name: t('packs.standard.name'),
            price: "$3.50",
            credits: 28000,
            tasks: 280,
            description: t('packs.standard.description'),
            variantIdKey: "LEMONSQUEEZY_VARIANT_ID_CREDITS_28000",
            features: [
                t('packs.standard.features.f1'),
                t('packs.standard.features.f2'),
                t('packs.standard.features.f3'),
                t('packs.standard.features.f4')
            ],
            color: "blue",
            highlight: false
        },
        {
            id: 'pro',
            name: t('packs.pro.name'),
            price: "$7.00",
            credits: 50000,
            tasks: 500,
            description: t('packs.pro.description'),
            variantIdKey: "LEMONSQUEEZY_VARIANT_ID_CREDITS_50000",
            features: [
                t('packs.pro.features.f1'),
                t('packs.pro.features.f2'),
                t('packs.pro.features.f3'),
                t('packs.pro.features.f4')
            ],
            color: "amber",
            highlight: true,
            badge: t('packs.pro.badge')
        },
        {
            id: 'business',
            name: t('packs.business.name'),
            price: "$14.00",
            credits: 120000,
            tasks: 1200,
            description: t('packs.business.description'),
            variantIdKey: "LEMONSQUEEZY_PRODUCT_ID_14",
            features: [
                t('packs.business.features.f1'),
                t('packs.business.features.f2'),
                t('packs.business.features.f3'),
                t('packs.business.features.f4')
            ],
            color: "purple",
            highlight: false
        }
    ];

    const handleBuy = async (pack: typeof creditPacks[0]) => {
        setLoading(pack.id);
        try {
            await withLoading(async () => {
                const response = await fetch("/api/lemonsqueezy/checkout", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        packId: pack.id,
                        redirectUrl: `${window.location.origin}/${locale}/dashboard`
                    })
                });

                if (!response.ok) {
                    const errorData = await response.text();
                    console.error("[Checkout] Response error:", errorData);

                    if (response.status === 401) {
                        toast.error("Please login to purchase credits");
                        router.push("/login?redirect=/tools");
                        return;
                    }
                    throw new Error(errorData || "Checkout failed");
                }

                const data = await response.json();
                if (data.url) {
                    window.location.href = data.url;
                } else {
                    toast.error("Could not generate checkout link");
                }
            }, t('processing') || 'Preparing checkout...');
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[hsl(var(--color-background))]">
            <Header locale={locale} />
            <main id="main-content" className="flex-1">
                <div className="py-24 bg-background transition-colors">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Header */}
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6" dangerouslySetInnerHTML={{ __html: t.raw('title') }} />
                            <p className="text-xl text-muted-foreground mb-8">
                                {t('subtitle')}
                            </p>
                        </div>

                        {/* Pricing Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
                            {creditPacks.map((pack) => (
                                <Card
                                    key={pack.id}
                                    className={`relative flex bg-[hsl(var(--color-card))] flex-col p-6 rounded-3xl transition-all duration-300 border-border
                                        ${pack.highlight
                                            ? 'bg-[hsl(var(--color-card))] ring-2 ring-primary shadow-2xl scale-105 z-10'
                                            : 'bg-[hsl(var(--color-card))] hover:shadow-xl hover:-translate-y-1'
                                        }
                                    `}
                                >
                                    {pack.highlight && (
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-500 text-primary-foreground text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">
                                            {pack.badge}
                                        </div>
                                    )}

                                    <div className="mb-6">
                                        <h3 className="text-lg font-bold text-muted-foreground uppercase tracking-wider mb-2">{pack.name}</h3>
                                        <div className="flex items-baseline gap-1 mb-4">
                                            <span className="text-4xl font-black text-foreground">{pack.price}</span>
                                            <span className="text-sm font-medium text-muted-foreground">{t('perPack')}</span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Coins className={`h-5 w-5 ${pack.color === 'amber' ? 'text-amber-500' : 'text-primary'}`} />
                                            <span className="text-xl font-bold text-foreground">{pack.credits.toLocaleString()} {t('credits')}</span>
                                        </div>
                                        <div className="text-sm font-medium text-muted-foreground bg-muted/50 px-3 py-1 rounded-lg inline-block">
                                            ≈ {pack.tasks.toLocaleString()} {t('documents')}
                                        </div>
                                    </div>

                                    <ul className="space-y-3 mb-8 flex-1">
                                        {pack.features.map((feature: string, i: number) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <Check className="h-5 w-5 text-emerald-500 shrink-0" />
                                                <span className="text-sm text-muted-foreground">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Button
                                        onClick={() => handleBuy(pack)}
                                        disabled={loading === pack.id}
                                        className={`w-full py-6 font-black rounded-2xl transition-all bg-color-primary text-white hover:opacity-90 shadow-lg shadow-primary/25`}
                                    >
                                        {loading === pack.id ? t('processing') : t('buy')}
                                    </Button>
                                </Card>
                            ))}
                        </div>

                        {/* Why Credits Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
                            <FeatureBox
                                icon={Zap}
                                title={t('features.deduct.title')}
                                desc={t('features.deduct.desc')}
                            />
                            <FeatureBox
                                icon={ShieldCheck}
                                title={t('features.noExpire.title')}
                                desc={t('features.noExpire.desc')}
                            />
                            <FeatureBox
                                icon={Coins}
                                title={t('features.transparent.title')}
                                desc={t('features.transparent.desc')}
                            />
                        </div>

                        {/* FAQ Section */}
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-2xl font-black text-center mb-10">{t('faq.title')}</h2>
                            <div className="space-y-4">
                                <FaqItem
                                    q={t('faq.items.fail.q')}
                                    a={t('faq.items.fail.a')}
                                />
                                <FaqItem
                                    q={t('faq.items.tools.q')}
                                    a={t('faq.items.tools.a')}
                                />
                                <FaqItem
                                    q={t('faq.items.subs.q')}
                                    a={t('faq.items.subs.a')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer locale={locale} />
        </div>
    );
}

function FeatureBox({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) {
    return (
        <div className="bg-card p-8 rounded-3xl border border-border/50 hover:-translate-y-1 hover:shadow-lg transition-all">
            <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4">
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="font-bold text-lg mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
        </div>
    );
}

function FaqItem({ q, a }: { q: string, a: string }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-6">
            <h4 className="font-bold text-foreground mb-2">{q}</h4>
            <p className="text-sm text-muted-foreground">{a}</p>
        </div>
    );
}
